import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog,
  DialogActions, DialogContent, DialogTitle, IconButton, MenuItem, Slider,
  Stack, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  LocalFireDepartment as FireIcon,
} from '@mui/icons-material';
import {
  Area, CartesianGrid, ComposedChart, Line, ReferenceLine,
  ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis,
} from 'recharts';
import { useTheme } from '@mui/material/styles';
import { chartTooltipProps } from '../components/charts/chartTooltip';
import {
  FireService,
  type FireSimulation,
  type RetirementPlan,
  type RetirementPlanRequest,
} from '../services/fire-service';

/** "0.05" (fraction, as stored) -> "5" (percent, as edited). */
const toPct = (fraction: string) => String(Number(fraction) * 100);
/** "5" (percent) -> "0.05" (fraction). */
const toFraction = (pct: string) => String(Number(pct) / 100);

const emptyForm = (): Record<string, string> => ({
  name: '',
  annual_expenses: '60000',
  expected_return: '5',
  return_stddev: '12',
  inflation: '2',
  retirement_date: `${new Date().getFullYear() + 15}-01-01`,
  withdrawal_rate: '4',
  monthly_contribution: '1000',
});

/**
 * FIRE / retirement planner.
 *
 * A Monte Carlo simulation seeded from the household's real net worth. The
 * chart is a DISTRIBUTION under the plan's stated assumptions — bands, not a
 * forecast. Levers re-simulate without saving, so "what if I saved more?" is
 * a slider drag, not an edit.
 */
export const FirePage: React.FC = () => {
  const theme = useTheme();
  const [plans, setPlans] = useState<RetirementPlan[]>([]);
  const [planId, setPlanId] = useState('');
  const [sim, setSim] = useState<FireSimulation | null>(null);
  const [loading, setLoading] = useState(true);
  const [simLoading, setSimLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [real, setReal] = useState(true);

  // Levers, initialised from the plan and re-simulated on change.
  const [contribution, setContribution] = useState(0);
  const [expectedReturn, setExpectedReturn] = useState(5);
  const [retireYear, setRetireYear] = useState(new Date().getFullYear() + 15);
  const debounceRef = useRef<number | undefined>(undefined);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<RetirementPlan | null>(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const plan = plans.find((p) => p.id === planId) ?? null;

  const loadPlans = useCallback(async () => {
    setLoading(true);
    try {
      const ls = await FireService.listPlans();
      setPlans(ls);
      setPlanId((prev) => (ls.some((p) => p.id === prev) ? prev : ls[0]?.id ?? ''));
    } catch (e: any) {
      setError(e?.message ?? 'Failed to load plans');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void loadPlans(); }, [loadPlans]);

  // Selecting a plan resets the levers to its saved values.
  useEffect(() => {
    if (!plan) return;
    setContribution(Number(plan.monthly_contribution));
    setExpectedReturn(Number(plan.expected_return) * 100);
    setRetireYear(Number(plan.retirement_date.slice(0, 4)));
  }, [plan]);

  // Debounced re-simulation: sliders fire continuously, and each simulate is
  // 5000 paths of backend work — one request per settled value is plenty.
  useEffect(() => {
    if (!plan) { setSim(null); return; }
    window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      setSimLoading(true);
      setError(null);
      FireService.simulate(plan.id, {
        monthly_contribution: String(contribution),
        expected_return: String(expectedReturn / 100),
        retirement_date: `${retireYear}${plan.retirement_date.slice(4, 10)}`,
      })
        .then(setSim)
        .catch((e: any) => setError(e?.message ?? 'Simulation failed'))
        .finally(() => setSimLoading(false));
    }, 400);
    return () => window.clearTimeout(debounceRef.current);
  }, [plan, contribution, expectedReturn, retireYear]);

  const money = (v: string | number) =>
    new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: plan?.currency || 'CAD',
      maximumFractionDigits: 0,
    }).format(Number(v));

  const compact = (v: number) =>
    new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 }).format(v);

  // Chart rows: quarterly samples keep ~200 points instead of ~600 — the
  // bands are smooth enough that monthly resolution adds nothing visible.
  const chartData = useMemo(() => {
    if (!sim) return [];
    const bands = real ? sim.real : sim.nominal;
    const startYear = new Date().getFullYear();
    return bands
      .filter((b) => b.month % 3 === 0 || b.month === sim.months)
      .map((b) => ({
        month: b.month,
        year: startYear + b.month / 12,
        outer: [Number(b.p10), Number(b.p90)],
        inner: [Number(b.p25), Number(b.p75)],
        median: Number(b.p50),
        worst: Number(sim.worst_decile_path[b.month] ?? null),
      }));
  }, [sim, real]);

  const retirementYear = sim
    ? new Date().getFullYear() + sim.retirement_month / 12
    : null;

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setDialogOpen(true);
  };

  const openEdit = (p: RetirementPlan) => {
    setEditing(p);
    setForm({
      name: p.name,
      annual_expenses: p.annual_expenses,
      expected_return: toPct(p.expected_return),
      return_stddev: toPct(p.return_stddev),
      inflation: toPct(p.inflation),
      retirement_date: p.retirement_date.slice(0, 10),
      withdrawal_rate: toPct(p.withdrawal_rate),
      monthly_contribution: p.monthly_contribution,
    });
    setDialogOpen(true);
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const body: RetirementPlanRequest = {
        name: form.name,
        annual_expenses: form.annual_expenses,
        expected_return: toFraction(form.expected_return),
        return_stddev: toFraction(form.return_stddev),
        inflation: toFraction(form.inflation),
        retirement_date: form.retirement_date,
        withdrawal_rate: toFraction(form.withdrawal_rate),
        monthly_contribution: form.monthly_contribution || '0',
      };
      const saved = editing
        ? await FireService.updatePlan(editing.id, body)
        : await FireService.createPlan(body);
      setDialogOpen(false);
      await loadPlans();
      setPlanId(saved.id);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to save plan');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p: RetirementPlan) => {
    if (!window.confirm(`Delete plan "${p.name}"?`)) return;
    try {
      await FireService.deletePlan(p.id);
      await loadPlans();
    } catch (e: any) {
      setError(e?.message ?? 'Failed to delete plan');
    }
  };

  const successPct = sim ? (Number(sim.success_rate) * 100).toFixed(1) : null;
  const successColor = sim
    ? Number(sim.success_rate) >= 0.9 ? 'success' : Number(sim.success_rate) >= 0.75 ? 'warning' : 'error'
    : 'default';

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <FireIcon color="primary" />
          <Typography variant="h5">FIRE planner</Typography>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          {plans.length > 0 && (
            <TextField
              select size="small" label="Plan" value={planId}
              onChange={(e) => setPlanId(e.target.value)} sx={{ minWidth: 200 }}
            >
              {plans.map((p) => (
                <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
              ))}
            </TextField>
          )}
          {plan && (
            <>
              <Tooltip title="Edit plan">
                <IconButton size="small" onClick={() => openEdit(plan)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete plan">
                <IconButton size="small" onClick={() => void remove(plan)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            New plan
          </Button>
        </Stack>
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 760 }}>
        A Monte Carlo simulation seeded from your current net worth: assumptions
        in, a distribution of outcomes out. The shaded bands show where 80% and
        50% of simulated futures land — this is a range of possibility under
        your own assumptions, not a forecast.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : plans.length === 0 ? (
        <Card variant="outlined">
          <CardContent>
            <Typography color="text.secondary">
              No plans yet. Create one with your expected spending, return
              assumptions and a retirement date — then drag the levers to see
              what actually moves the needle.
            </Typography>
          </CardContent>
        </Card>
      ) : plan && (
        <>
          {sim && (
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
              <Card sx={{ flex: 1 }}>
                <CardContent>
                  <Typography variant="overline" color="text.secondary">Success rate</Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography className="fw-num" variant="h4">{successPct}%</Typography>
                    <Chip size="small" color={successColor as any}
                      label={`${(Number(sim.success_rate) * 5000).toFixed(0)} of 5000 paths last`} />
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    Fraction of simulated futures where the money never runs out
                    within the {Math.round(sim.months / 12)}-year horizon.
                  </Typography>
                </CardContent>
              </Card>
              <Card sx={{ flex: 1 }}>
                <CardContent>
                  <Typography variant="overline" color="text.secondary">Starting from</Typography>
                  <Typography className="fw-num" variant="h4">{money(sim.start_value)}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Your current ledger net worth — the simulation's month zero.
                  </Typography>
                </CardContent>
              </Card>
              <Card sx={{ flex: 1 }}>
                <CardContent>
                  <Typography variant="overline" color="text.secondary">FIRE number</Typography>
                  <Typography className="fw-num" variant="h4">{money(sim.fire_number)}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Annual expenses ÷ withdrawal rate ({toPct(plan.withdrawal_rate)}%).
                  </Typography>
                </CardContent>
              </Card>
            </Stack>
          )}

          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="overline" color="text.secondary">
                  Projected portfolio value
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  {simLoading && <CircularProgress size={16} />}
                  <ToggleButtonGroup size="small" exclusive value={real ? 'real' : 'nominal'}
                    onChange={(_, v) => v && setReal(v === 'real')}>
                    <ToggleButton value="real">Today's dollars</ToggleButton>
                    <ToggleButton value="nominal">Future dollars</ToggleButton>
                  </ToggleButtonGroup>
                </Stack>
              </Stack>

              <Box sx={{ height: 380 }}>
                <ResponsiveContainer>
                  <ComposedChart data={chartData} margin={{ top: 8, right: 16, bottom: 0, left: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                    <XAxis
                      dataKey="year" type="number" domain={['dataMin', 'dataMax']}
                      tickFormatter={(v: number) => String(Math.round(v))}
                      tick={{ fontSize: 12 }}
                    />
                    <YAxis tickFormatter={(v: number) => compact(v)} tick={{ fontSize: 12 }} width={64} />
                    <ChartTooltip
                      {...chartTooltipProps}
                      formatter={(value: any, name: any) => {
                        if (Array.isArray(value)) return [`${money(value[0])} – ${money(value[1])}`, name];
                        return [money(value), name];
                      }}
                      labelFormatter={(v: any) => `Year ${Math.round(Number(v))}`}
                    />
                    {/* p10–p90 fan, then the darker p25–p75 core on top. */}
                    <Area dataKey="outer" name="10th–90th percentile" stroke="none"
                      fill={theme.palette.primary.main} fillOpacity={0.12} isAnimationActive={false} />
                    <Area dataKey="inner" name="25th–75th percentile" stroke="none"
                      fill={theme.palette.primary.main} fillOpacity={0.22} isAnimationActive={false} />
                    <Line dataKey="median" name="Median" dot={false} strokeWidth={2}
                      stroke={theme.palette.primary.main} isAnimationActive={false} />
                    {/* One concrete unlucky path: sequence-of-returns made visible. */}
                    <Line dataKey="worst" name="Unlucky path (p10 ending)" dot={false} strokeWidth={1}
                      strokeDasharray="4 3" stroke={theme.palette.error.main} isAnimationActive={false} />
                    {retirementYear && (
                      <ReferenceLine x={retirementYear} stroke={theme.palette.text.secondary}
                        strokeDasharray="3 3"
                        label={{ value: 'Retirement', fontSize: 11, position: 'insideTopRight' }} />
                    )}
                  </ComposedChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                Levers — drag to re-simulate (nothing is saved)
              </Typography>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={4} sx={{ mt: 1 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" gutterBottom>
                    Monthly contribution: <b>{money(contribution)}</b>
                  </Typography>
                  <Slider
                    value={contribution} min={0} max={10000} step={100}
                    onChange={(_, v) => setContribution(v as number)}
                    valueLabelDisplay="auto"
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" gutterBottom>
                    Expected return: <b>{expectedReturn.toFixed(1)}%</b>
                  </Typography>
                  <Slider
                    value={expectedReturn} min={0} max={12} step={0.25}
                    onChange={(_, v) => setExpectedReturn(v as number)}
                    valueLabelDisplay="auto"
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" gutterBottom>
                    Retirement year: <b>{retireYear}</b>
                  </Typography>
                  <Slider
                    value={retireYear}
                    min={new Date().getFullYear()}
                    max={new Date().getFullYear() + 40}
                    step={1}
                    onChange={(_, v) => setRetireYear(v as number)}
                    valueLabelDisplay="auto"
                  />
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit plan' : 'New retirement plan'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Name" placeholder="Retire at 55" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} fullWidth
            />
            <Stack direction="row" spacing={2}>
              <TextField
                label="Annual expenses (today's $)" value={form.annual_expenses} fullWidth
                onChange={(e) => setForm({ ...form, annual_expenses: e.target.value })}
              />
              <TextField
                label="Monthly contribution" value={form.monthly_contribution} fullWidth
                onChange={(e) => setForm({ ...form, monthly_contribution: e.target.value })}
              />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Expected return %" value={form.expected_return} fullWidth
                onChange={(e) => setForm({ ...form, expected_return: e.target.value })}
                helperText="Long-run nominal, e.g. 5"
              />
              <TextField
                label="Volatility (stddev) %" value={form.return_stddev} fullWidth
                onChange={(e) => setForm({ ...form, return_stddev: e.target.value })}
                helperText="Annual, e.g. 12"
              />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Inflation %" value={form.inflation} fullWidth
                onChange={(e) => setForm({ ...form, inflation: e.target.value })}
              />
              <TextField
                label="Withdrawal rate %" value={form.withdrawal_rate} fullWidth
                onChange={(e) => setForm({ ...form, withdrawal_rate: e.target.value })}
                helperText="Sets the FIRE number, e.g. 4"
              />
            </Stack>
            <TextField
              label="Retirement date" type="date" value={form.retirement_date}
              onChange={(e) => setForm({ ...form, retirement_date: e.target.value })}
              InputLabelProps={{ shrink: true }} fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained" onClick={() => void save()}
            disabled={saving || !form.name || !form.annual_expenses}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
